import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Play, Share2, Copy, CheckCircle, ExternalLink, Twitter,
  Star, MapPin, Mail, Users, ArrowRight, Ruler
} from 'lucide-react';
import { API } from '../lib/constants';

const DemoBento = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [stats, setStats] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/profile/dcook1`);
        setProfile(res.data);
        setHighlights(res.data.highlights || []);
        const userId = res.data.profile?.user_id;
        if (userId) {
          const [offersRes, statsRes] = await Promise.all([
            axios.get(`${API}/api/dashboard/${userId}/offers`).catch(() => ({ data: { offers: [] } })),
            axios.get(`${API}/api/dashboard/${userId}/comprehensive-stats`).catch(() => ({ data: { stats: [] } })),
          ]);
          setOffers(offersRes.data.offers || []);
          setStats(statsRes.data.stats || []);
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, []);

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#f5f5f0] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const p = profile.profile;
  const measurables = profile.measurables || {};

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const measurablesData = [
    { label: 'HEIGHT', value: p.height },
    { label: 'WEIGHT', value: p.weight ? `${p.weight}` : null },
    { label: '40-YD', value: measurables?.forty_yard_dash ? `${measurables.forty_yard_dash}s` : null },
    { label: 'BENCH', value: measurables?.bench_press ? `${measurables.bench_press}` : null },
    { label: 'SQUAT', value: measurables?.squat ? `${measurables.squat}` : null },
    { label: 'VERT', value: measurables?.vertical_jump ? `${measurables.vertical_jump}"` : null },
  ].filter(s => s.value);

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      {/* Demo Banner */}
      <div className="bg-[#0F5132] text-white text-center py-2 px-4 text-xs font-bold sticky top-0 z-50">
        ⚡ DEMO: Bento Grid Layout — <button onClick={() => navigate('/')} className="underline">Back to Home</button>
        {' '}| <button onClick={() => navigate('/demo/card')} className="underline">← View Card Layout</button>
      </div>

      <div className="max-w-xl lg:max-w-5xl mx-auto px-3 lg:px-6 py-6">
        
        {/* ═══ BENTO GRID ═══ */}
        <div className="grid grid-cols-4 lg:grid-cols-12 gap-2.5 lg:gap-4 auto-rows-auto">
          
          {/* ── HERO TILE ── */}
          <div className="col-span-4 lg:col-span-8 bg-[#1a1a1a] rounded-3xl p-6 lg:p-8 relative overflow-hidden">
            <div className="flex items-center gap-5 lg:gap-8">
              <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-36 lg:h-36 rounded-2xl overflow-hidden ring-3 ring-[#16A34A]/30 flex-shrink-0">
                {p.profile_picture_url ? (
                  <img src={p.profile_picture_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#16A34A] flex items-center justify-center text-3xl font-black text-black">{p.name?.charAt(0)}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-none mb-1 tracking-tight">{p.name}</h1>
                <p className="text-white/40 text-sm lg:text-base">{p.school}</p>
                <div className="flex items-center gap-2 mt-1 text-white/30 text-xs lg:text-sm">
                  <span>Class of {p.class_year}</span>
                  {p.city && <><span>•</span><MapPin className="h-3 w-3" /><span>{p.city}, {p.state}</span></>}
                </div>
                <div className="flex gap-1.5 mt-3">
                  {p.positions?.map((pos, i) => (
                    <span key={i} className="bg-[#0F5132] text-white text-xs lg:text-sm font-black px-3 lg:px-4 py-1 lg:py-1.5 rounded-full">{pos}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -right-4 -top-4 w-32 h-32 lg:w-48 lg:h-48 bg-[#16A34A]/5 rounded-full" />
          </div>

          {/* ── STATS TILES (desktop: stacked on right) ── */}
          <div className="col-span-2 lg:col-span-2 bg-white rounded-2xl p-4 lg:p-5 flex flex-col items-center justify-center">
            <p className="text-3xl lg:text-4xl font-black text-[#1a1a1a]">{p.height || '—'}</p>
            <p className="text-gray-400 text-[10px] lg:text-xs uppercase tracking-[0.15em] mt-0.5">Height</p>
          </div>
          <div className="col-span-2 lg:col-span-2 bg-white rounded-2xl p-4 lg:p-5 flex flex-col items-center justify-center">
            <p className="text-3xl lg:text-4xl font-black text-[#1a1a1a]">{p.weight || '—'} <span className="text-lg text-gray-400">lbs</span></p>
            <p className="text-gray-400 text-[10px] lg:text-xs uppercase tracking-[0.15em] mt-0.5">Weight</p>
          </div>

          {/* ── OFFERS TILE (spans 4 cols) ── */}
          {offers.length > 0 && (
            <div className="col-span-4 lg:col-span-5 bg-white rounded-2xl p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="h-5 w-5 text-[#16A34A]" />
                <h2 className="text-xl font-black text-[#1a1a1a]">Offers</h2>
                <span className="bg-[#16A34A]/15 text-[#1a1a1a] text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{offers.length}</span>
              </div>
              <div className="space-y-2">
                {offers.map((offer, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    {offer.college?.logoUrl ? (
                      <img src={offer.college.logoUrl} alt="" className="w-10 h-10 rounded-lg object-contain bg-white p-1 border border-gray-100" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 font-bold text-xs">?</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1a1a1a] text-sm">{offer.college?.name || 'University'}</p>
                      <p className="text-gray-400 text-xs">{offer.college?.division}</p>
                    </div>
                    <Badge className={`text-xs font-bold border-0 ${
                      offer.status === 'committed' ? 'bg-green-100 text-green-700' :
                      offer.status === 'offer' ? 'bg-[#16A34A]/20 text-[#1a1a1a]' : 'bg-gray-100 text-gray-500'
                    }`}>{offer.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── HIGHLIGHTS TILE (spans 4 cols) ── */}
          {highlights.length > 0 && (
            <div className="col-span-4 lg:col-span-7 bg-[#1a1a1a] rounded-2xl p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Play className="h-5 w-5 text-[#16A34A]" />
                <h2 className="text-xl font-black text-white">Highlights</h2>
                <span className="bg-white/10 text-white/50 text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{highlights.length}</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
                {highlights.slice(0, 4).map((vid, i) => (
                  <a key={i} href={vid.hudl_video_url} target="_blank" rel="noopener noreferrer"
                    className={`rounded-xl overflow-hidden relative group ${i === 0 ? 'col-span-2' : ''}`}>
                    <div className={`${i === 0 ? 'aspect-video' : 'aspect-square'} bg-white/5`}>
                      {vid.og_image ? (
                        <img src={vid.og_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Play className="h-8 w-8 text-white/10" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-white text-xs font-bold truncate">{vid.video_title || `Highlight ${i + 1}`}</p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-10 h-10 bg-[#16A34A] rounded-full flex items-center justify-center">
                          <Play className="h-4 w-4 text-black ml-0.5" />
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* ── MEASURABLES GRID (individual tiles) ── */}
          {measurablesData.length > 2 && (
            <>
              <div className="col-span-4 lg:col-span-12 pt-1">
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-gray-400" />
                  <p className="text-sm font-bold text-gray-500">Measurables</p>
                </div>
              </div>
              {measurablesData.slice(2).map((stat, i) => (
                <div key={i} className="col-span-2 lg:col-span-3 bg-white rounded-2xl p-4 lg:p-5 text-center">
                  <p className="text-2xl font-black text-[#1a1a1a]">{stat.value}</p>
                  <p className="text-gray-400 text-[10px] uppercase tracking-[0.15em]">{stat.label}</p>
                </div>
              ))}
            </>
          )}

          {/* ── SEASON STATS TILE ── */}
          {stats.length > 0 && (
            <div className="col-span-4 lg:col-span-5 bg-white rounded-2xl p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-5 w-5 text-[#16A34A]" />
                <h2 className="text-xl font-black text-[#1a1a1a]">{stats[0].season}</h2>
                <span className="bg-gray-100 text-gray-500 text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{stats[0].games_played} GP</span>
              </div>
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
                {stats[0].offensive_stats && Object.entries(stats[0].offensive_stats).filter(([_,v]) => v && v !== 0).map(([key, val]) => (
                  <div key={key} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-black text-[#1a1a1a]">{val}</p>
                    <p className="text-gray-400 text-[9px] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                  </div>
                ))}
                {stats[0].defensive_stats && Object.entries(stats[0].defensive_stats).filter(([_,v]) => v && v !== 0).map(([key, val]) => (
                  <div key={key} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-xl font-black text-[#1a1a1a]">{val}</p>
                    <p className="text-gray-400 text-[9px] uppercase tracking-wider">{key.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SOCIAL LINKS TILE ── */}
          <div className="col-span-4 lg:col-span-12 bg-white rounded-2xl p-4 lg:p-5">
            <div className="flex gap-2 flex-wrap justify-center">
              {p.hudl_url && (
                <a href={p.hudl_url} target="_blank" rel="noopener noreferrer"
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all">
                  <ExternalLink className="h-3 w-3" /> HUDL
                </a>
              )}
              <button className="bg-[#0F5132] hover:bg-[#0B3B24] text-white px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 transition-all">
                <Users className="h-3 w-3" /> Follow
              </button>
              <button className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all">
                <Mail className="h-3 w-3" /> Message
              </button>
              <button onClick={copyLink}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all">
                {copied ? <CheckCircle className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                {copied ? 'Copied!' : 'Share'}
              </button>
            </div>
          </div>

          {/* ── TAGLINE TILE ── */}
          {p.tagline && (
            <div className="col-span-4 lg:col-span-12 bg-[#16A34A] rounded-2xl p-5 text-center">
              <p className="text-[#1a1a1a] font-black text-lg italic">"{p.tagline}"</p>
            </div>
          )}

          {/* ── CTA TILE ── */}
          <div className="col-span-4 lg:col-span-12 bg-[#1a1a1a] rounded-2xl p-6 lg:p-8 text-center">
            <div className="w-10 h-10 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-black font-black text-lg">O</span>
            </div>
            <h3 className="text-white font-black text-xl mb-1">Get your own Offer profile</h3>
            <p className="text-white/30 text-sm mb-4">The link in bio built for athletes</p>
            <button onClick={() => navigate('/create-profile')}
              className="bg-[#0F5132] text-white font-bold px-8 py-3 rounded-full text-sm hover:bg-[#0B3B24] transition-all">
              Create Profile — Free <ArrowRight className="inline h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoBento;
